// Session-replay privacy for the logged-in app (Travis, Oct 4, 2026: record
// every /app session; build file §3c: never send member/tenant data, message
// bodies, door codes or financial details to PostHog).
//
// Default-deny: every /app page is MASKED in replays (all text replaced, all
// images/video/canvas blocked) unless it is on the explicit READABLE list
// below — setup, billing, research and configuration screens with no tenant
// data, no message bodies, no door codes and no P&L numbers. A page added
// later is masked automatically until someone reviews it and adds it here.
// Typed input values are always masked by the shared PostHog init.
//
// Mechanism: posthog-js's recorder masks text under the `ph-mask` class and
// blocks elements matching its blockSelector / the `ph-no-capture` class. On
// masked pages the class sits on <body>, so portals (dialogs, sheets,
// popovers) are covered too. Shared chrome that can show member data on any
// page (notification panel, global search results) carries `ph-mask` itself.

import { useLayoutEffect } from "react";

export const REPLAY_MASK_CLASS = "ph-mask";

/** Pages that are safe to record readable. Everything else under /app is masked. */
export const REPLAY_READABLE_PATTERNS: readonly RegExp[] = [
  // Signup, onboarding, PadSplit connection — the funnel we most need to see.
  /^\/app\/(login|verify|totp-verify|totp-enroll)$/,
  /^\/app\/(onboarding|welcome|setup|connect-padsplit|padsplit-sync)(\/.*)?$/,
  // Billing / plan / own profile / messaging provider settings.
  /^\/app\/account\/(billing|plan|messaging)$/,
  /^\/app\/profile$/,
  // Team and configuration screens (no tenant data).
  /^\/app\/(team|team-stats)$/,
  /^\/app\/(tasks|maintenance)\/settings$/,
  /^\/app\/operations\/(task-rules|service-forms|vendors\/settings)$/,
  /^\/app\/messages\/(settings|rules(\/.*)?)$/,
  /^\/app\/members\/booking-requests\/settings$/,
  /^\/app\/pricing\/models(\/.*)?$/,
  /^\/app\/underwriting\/defaults$/,
  // Property list (addresses, not tenants). Property detail stays masked.
  /^\/app\/properties$/,
  // Research and directories (market/listing data, lenders, realtors).
  /^\/app\/(research|buy-box)(\/.*)?$/,
  /^\/app\/(finance|find-realtor)$/,
  // Tools and help.
  /^\/app\/(image-generator|prompt-library|supplies|refer|faq)$/,
  /^\/app\/resources\/mercury$/,
];

export function isReplayReadablePath(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  return REPLAY_READABLE_PATTERNS.some((re) => re.test(path));
}

/** Toggle whole-page replay masking for the current /app path (before the recorder sees the DOM). */
export function useReplayPrivacy(pathname: string): void {
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const masked = pathname.startsWith("/app") && !isReplayReadablePath(pathname);
    document.body.classList.toggle(REPLAY_MASK_CLASS, masked);
    return () => {
      document.body.classList.remove(REPLAY_MASK_CLASS);
    };
  }, [pathname]);
}
